import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import tiffinOrderService from '../services/tiffinOrderService';
import tiffinAdminService from '../services/tiffinAdminService';
import {
  setMenuItems,
  setMenuLoading,
  setMenuError,
  addToCart,
  removeFromCart,
  setCartItemQuantity,
  clearCart,
  setCustomerInfo,
  setOrder,
  setOrderLoading,
  setOrderError,
} from '../../../redux/slices/tiffinSlice';

const TiffinMenu = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { menuItems, menuLoading, menuError, menuTitle, cart, customerInfo, orderLoading, orderError } = useSelector((state) => state.tiffin);
  const { user, isAuthenticated } = useSelector((state) => state.auth);

  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Menu selector
  const [availableMenus, setAvailableMenus] = useState([]);
  const [selectedMenuId, setSelectedMenuId] = useState(null);

  // Pre-fill customer info from auth if logged in
  useEffect(() => {
    if (isAuthenticated && user) {
      dispatch(setCustomerInfo({
        firstName: user?.User_First_Name || user?.user_First_Name || user?.firstName || '',
        lastName: user?.User_Last_Name || user?.user_Last_Name || user?.lastName || '',
        phone: user?.User_Phone || user?.User_Cell_Phone_No || user?.user_Phone || '',
        email: user?.User_Email || user?.user_Email || user?.email || '',
      }));
    }
  }, [isAuthenticated, user, dispatch]);

  // Load available menus list
  useEffect(() => {
    tiffinAdminService.getMenus().then(data => {
      const sorted = (data || []).sort((a, b) => new Date(b.menuDate) - new Date(a.menuDate));
      setAvailableMenus(sorted);
      if (sorted.length > 0) setSelectedMenuId(sorted[0].tiffinMenu_Id);
    }).catch(e => console.error('Failed to load menus list', e));
  }, []);

  // Fetch active menu by selected menu date
  const fetchMenu = useCallback(async (menuDate) => {
    if (!menuDate) return;
    dispatch(setMenuLoading(true));
    dispatch(setMenuError(null));
    try {
      const data = await tiffinOrderService.getActiveMenu(menuDate);
      dispatch(setMenuItems(data || []));
    } catch (err) {
      dispatch(setMenuError('Failed to load menu. Please try again later.'));
      console.error(err);
    } finally {
      dispatch(setMenuLoading(false));
    }
  }, [dispatch]);

  useEffect(() => {
    if (selectedMenuId && availableMenus.length > 0) {
      const m = availableMenus.find(x => x.tiffinMenu_Id === selectedMenuId);
      if (m) fetchMenu(m.menuDate?.split('T')[0]);
    }
  }, [selectedMenuId, availableMenus, fetchMenu]);

  // Cart calculations
  const cartItems = Object.values(cart);
  const cartTotal = cartItems.reduce((sum, c) => sum + ((c.item.price || 0) * c.quantity), 0);
  const cartCount = cartItems.reduce((sum, c) => sum + c.quantity, 0);

  const getItemQuantity = (itemId) => {
    return cart[itemId]?.quantity || 0;
  };

  const handleAdd = (item) => {
    dispatch(addToCart(item));
  };

  const handleRemove = (itemId) => {
    dispatch(removeFromCart(itemId));
  };

  const handleQuantityChange = (itemId, qty) => {
    const num = parseInt(qty) || 0;
    dispatch(setCartItemQuantity({ id: itemId, quantity: num }));
  };

  const handleCustomerChange = (e) => {
    const { name, value } = e.target;
    dispatch(setCustomerInfo({ [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors(prev => { const n = { ...prev }; delete n[name]; return n; });
    }
  };

  const validateCustomerInfo = () => {
    const errors = {};
    if (!customerInfo.firstName?.trim()) errors.firstName = 'First name is required';
    if (!customerInfo.lastName?.trim()) errors.lastName = 'Last name is required';
    if (!customerInfo.email?.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(customerInfo.email)) {
      errors.email = 'Please enter a valid email';
    }
    if (!customerInfo.phone?.trim()) {
      errors.phone = 'Phone is required for WhatsApp notification';
    }
    return errors;
  };

  const handleProceedToPayment = async () => {
    if (cartCount === 0) return;

    // Show customer form if not visible
    if (!showCustomerForm) {
      setShowCustomerForm(true);
      return;
    }

    // Validate
    const errors = validateCustomerInfo();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    dispatch(setOrderLoading(true));
    dispatch(setOrderError(null));

    try {
      // Build order items from cart
      const items = cartItems.map(c => ({
        tiffinMenuItem_Id: c.item.tiffinMenuItem_Id,
        itemName: c.item.itemName,
        quantity: c.quantity,
        price: c.item.price,
      }));

      const orderInput = {
        firstName: customerInfo.firstName,
        lastName: customerInfo.lastName,
        phone: customerInfo.phone,
        email: customerInfo.email,
        notes: customerInfo.notes,
        userId: user?.User_Id || user?.user_Id || user?.userId || null,
        orgId: 3,
        items,
      };

      const result = await tiffinOrderService.createOrder(orderInput);
      dispatch(setOrder(result));

      // Navigate to payment page
      navigate('/tiffin/payment');
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to create order. Please try again.';
      dispatch(setOrderError(msg));
      console.error(err);
    } finally {
      dispatch(setOrderLoading(false));
    }
  };

  return (
    <div className="min-h-screen" style={{background: 'linear-gradient(180deg, #faf8f5 0%, #f5f0eb 40%, #faf8f5 100%)'}}>
      {/* Hero Header */}
      <section className="relative" style={{background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(250,245,238,0.9) 100%)', backdropFilter: 'saturate(180%) blur(20px)', WebkitBackdropFilter: 'saturate(180%) blur(20px)', borderBottom: '1px solid rgba(180,160,130,0.15)', boxShadow: '0 1px 12px rgba(120,80,40,0.04)'}}>
        <div className="container-custom py-10 md:py-14">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-5">
              <button
                onClick={() => navigate('/')}
                className="mt-1 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-105"
                style={{background: 'rgba(255,255,255,0.8)', border: '1px solid rgba(180,160,130,0.2)', boxShadow: '0 2px 8px rgba(120,80,40,0.06), 0 1px 2px rgba(0,0,0,0.04)'}}
              >
                <svg className="w-5 h-5 text-stone-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              </button>
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <span
                    className="inline-flex items-center gap-2 font-semibold text-xs px-4 py-2 rounded-full"
                    style={{background: 'linear-gradient(135deg, #fdf6ee 0%, #faf0e4 100%)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', border: '1px solid rgba(210,190,160,0.3)', boxShadow: '0 2px 8px rgba(160,130,80,0.08), inset 0 1px 0 rgba(255,255,255,0.7)', color: '#5c4a2f'}}
                  >
                    <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{background: 'linear-gradient(135deg, #b45309, #d97706)'}}></span>
                    {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                  </span>
                  {menuItems.length > 0 && (
                    <span className="text-xs font-medium" style={{color: '#a8a29e'}}>{menuItems.length} items available</span>
                  )}
                </div>
                <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{color: '#44403c', letterSpacing: '-0.02em'}}>
                  {menuTitle || "Today's Special Menu"}
                </h1>
                <p className="mt-2 text-base" style={{color: '#a8a29e'}}>
                  Fresh, homemade vegetarian meals prepared with love
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* Decorative line */}
        <div className="absolute bottom-0 left-0 right-0 h-px" style={{background: 'linear-gradient(90deg, transparent 0%, rgba(180,140,80,0.15) 50%, transparent 100%)'}}></div>
      </section>

      {/* Menu Selector Pills */}
      {availableMenus.length > 0 && (
        <div style={{background: 'rgba(250,245,238,0.8)', borderBottom: '1px solid rgba(180,160,130,0.12)'}}>
          <div className="container-custom py-3">
            <div className="flex items-center gap-2 overflow-x-auto pb-1" style={{scrollbarWidth: 'none'}}>
              <span className="text-xs font-semibold text-stone-400 flex-shrink-0 mr-1">Menu:</span>
              {availableMenus.map(m => (
                <button
                  key={m.tiffinMenu_Id}
                  onClick={() => setSelectedMenuId(m.tiffinMenu_Id)}
                  className="flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full transition-all"
                  style={selectedMenuId === m.tiffinMenu_Id
                    ? {background: 'linear-gradient(135deg,#b45309,#d97706)', color: '#fff', boxShadow: '0 2px 8px rgba(180,83,9,0.25)'}
                    : {background: 'rgba(255,255,255,0.8)', color: '#78716c', border: '1px solid rgba(180,160,130,0.25)'}
                  }
                >
                  {m.menuTitle}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="container-custom py-8 md:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">

          {/* Menu Items - Left side */}
          <div className="lg:col-span-2">
            {menuLoading && (
              <div className="text-center py-20">
                <div className="inline-flex items-center gap-3 bg-orange-50 px-6 py-3 rounded-full">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-orange-200 border-t-orange-600"></div>
                  <span className="text-orange-700 font-medium text-sm">Loading today's menu...</span>
                </div>
              </div>
            )}

            {menuError && (
              <div className="bg-red-50 border border-red-100 rounded-2xl p-8 text-center">
                <div className="text-3xl mb-3">⚠️</div>
                <p className="text-red-700 font-semibold mb-4">{menuError}</p>
                <button onClick={fetchMenu} className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5 px-8 rounded-full transition shadow-sm">
                  Try Again
                </button>
              </div>
            )}

            {!menuLoading && !menuError && menuItems.length === 0 && (
              <div className="bg-gray-50 rounded-2xl p-16 text-center border border-gray-100">
                <div className="text-6xl mb-5">🍽️</div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">No Menu Available</h3>
                <p className="text-gray-500 leading-relaxed max-w-md mx-auto">
                  Today's menu hasn't been published yet. Please check back later.
                </p>
              </div>
            )}

            {!menuLoading && menuItems.length > 0 && (
              <div className="space-y-3">
                {menuItems.map((item, index) => {
                  const qty = getItemQuantity(item.tiffinMenuItem_Id);
                  return (
                    <div
                      key={item.tiffinMenuItem_Id}
                      className={`group relative rounded-2xl p-5 transition-all duration-300 hover:scale-[1.01]`}
                      style={{
                        background: qty > 0 ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.75)',
                        backdropFilter: 'blur(12px)',
                        WebkitBackdropFilter: 'blur(12px)',
                        border: qty > 0 ? '1px solid rgba(180,130,60,0.25)' : '1px solid rgba(180,160,130,0.12)',
                        boxShadow: qty > 0
                          ? '0 10px 30px rgba(120, 80, 40, 0.1), 0 2px 4px rgba(0,0,0,0.02), inset 0 1px 0 rgba(255,255,255,0.8)'
                          : '0 2px 12px rgba(120, 80, 40, 0.04), 0 1px 2px rgba(0,0,0,0.02), inset 0 1px 0 rgba(255,255,255,0.6)',
                      }}
                    >
                      {/* Item number badge */}
                      <div className="absolute -top-2.5 -left-2.5 w-7 h-7 text-[10px] font-bold rounded-full flex items-center justify-center" style={{background: 'linear-gradient(135deg, #fdf6ee, #f5e6d0)', color: '#6b4c0a', border: '1px solid rgba(200,170,110,0.35)', boxShadow: '0 3px 10px rgba(160, 130, 70, 0.12), inset 0 1px 0 rgba(255,255,255,0.7)'}}>
                        {index + 1}
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        {/* Item Image */}
                        {item.imageUrl && (
                          <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0" style={{border: '1px solid rgba(180,160,130,0.15)', boxShadow: '0 3px 10px rgba(120,80,40,0.08), inset 0 0 0 1px rgba(255,255,255,0.1)'}}>
                            <img src={item.imageUrl} alt={item.itemName} className="w-full h-full object-cover" />
                          </div>
                        )}

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="text-base font-semibold truncate" style={{color: '#292524'}}>{item.itemName}</h3>
                            {qty > 0 && (
                              <span className="flex-shrink-0 w-5 h-5 text-[10px] font-bold rounded-full flex items-center justify-center" style={{background: 'linear-gradient(135deg, #fdf6ee, #f5e6d0)', color: '#6b4c0a', border: '1px solid rgba(200,170,110,0.35)', boxShadow: '0 2px 6px rgba(160, 130, 70, 0.1)'}}>
                                {qty}
                              </span>
                            )}
                          </div>
                          {item.itemDescription && (
                            <p className="text-sm mt-0.5 leading-relaxed truncate" style={{color: '#a8a29e'}}>{item.itemDescription}</p>
                          )}
                          <p className="text-lg font-bold mt-1.5" style={{color: '#92400e'}}>${(item.price || 0).toFixed(2)}</p>
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          {qty === 0 ? (
                            <button
                              onClick={() => handleAdd(item)}
                              className="font-semibold py-2 px-5 rounded-xl transition-all duration-200 text-sm hover:scale-105 active:scale-95"
                              style={{background: 'linear-gradient(180deg, #fdf6ee 0%, #f5e6d0 100%)', color: '#6b4c0a', border: '1px solid rgba(200,170,110,0.35)', boxShadow: '0 4px 14px rgba(160, 130, 70, 0.12), 0 1px 3px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.7)'}}
                            >
                              Add +
                            </button>
                          ) : (
                            <div className="flex items-center gap-1 rounded-xl px-1 py-1" style={{background: 'rgba(250,245,238,0.8)', border: '1px solid rgba(180,160,130,0.15)', boxShadow: '0 2px 8px rgba(120, 80, 40, 0.04), inset 0 1px 0 rgba(255,255,255,0.5)'}}>
                              <button
                                onClick={() => handleRemove(item.tiffinMenuItem_Id)}
                                className="w-8 h-8 rounded-lg font-bold flex items-center justify-center hover:bg-red-50 hover:text-red-600 transition text-base"
                                style={{background: 'rgba(255,255,255,0.9)', border: '1px solid rgba(180,160,130,0.12)', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', color: '#78716c'}}
                              >
                                −
                              </button>
                              <input
                                type="number"
                                min="0"
                                value={qty}
                                onChange={(e) => handleQuantityChange(item.tiffinMenuItem_Id, e.target.value)}
                                className="w-10 text-center bg-transparent font-bold text-gray-800 text-sm border-none outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              />
                              <button
                                onClick={() => handleAdd(item)}
                                className="w-8 h-8 rounded-lg font-bold flex items-center justify-center transition text-base hover:scale-105 active:scale-95"
                                style={{background: 'linear-gradient(180deg, #fdf6ee 0%, #f5e6d0 100%)', color: '#6b4c0a', border: '1px solid rgba(200,170,110,0.35)', boxShadow: '0 3px 8px rgba(160, 130, 70, 0.12), inset 0 1px 0 rgba(255,255,255,0.7)'}}
                              >
                                +
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Cart / Order Summary - Right side */}
          <div className="lg:col-span-1">
            <div className="sticky top-6">
              <div className="rounded-2xl overflow-hidden" style={{background: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid rgba(180,160,130,0.12)', boxShadow: '0 10px 40px rgba(120, 80, 40, 0.07), 0 2px 6px rgba(0,0,0,0.02), inset 0 1px 0 rgba(255,255,255,0.5)'}}>
                {/* Cart header */}
                <div className="px-6 py-5" style={{borderBottom: '1px solid rgba(180,160,130,0.1)'}}>
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-bold flex items-center gap-2.5" style={{color: '#44403c'}}>
                      Your Order
                    </h2>
                    {cartCount > 0 && (
                      <span className="text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center" style={{background: 'linear-gradient(135deg, #fdf6ee, #f5e6d0)', color: '#6b4c0a', border: '1px solid rgba(200,170,110,0.35)', boxShadow: '0 2px 6px rgba(160, 130, 70, 0.1)'}}>
                        {cartCount}
                      </span>
                    )}
                  </div>
                </div>

                <div className="p-6">
                  {cartCount === 0 ? (
                    <div className="text-center py-10">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center" style={{background: 'rgba(250,245,238,0.6)', border: '1px solid rgba(180,160,130,0.1)', boxShadow: '0 4px 12px rgba(120, 80, 40, 0.04)'}}>
                        <span className="text-3xl">🍱</span>
                      </div>
                      <p className="text-gray-400 text-sm font-medium">Your cart is empty</p>
                      <p className="text-gray-300 text-xs mt-1">Add items from the menu to get started</p>
                    </div>
                  ) : (
                    <>
                      {/* Cart items */}
                      <div className="space-y-4 mb-5 max-h-72 overflow-y-auto pr-1">
                        {cartItems.map((c) => (
                          <div key={c.item.tiffinMenuItem_Id} className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-gray-900 text-sm truncate">{c.item.itemName}</p>
                              <p className="text-gray-400 text-xs mt-0.5">${(c.item.price || 0).toFixed(2)} × {c.quantity}</p>
                            </div>
                            <p className="font-bold text-gray-900 text-sm flex-shrink-0">${((c.item.price || 0) * c.quantity).toFixed(2)}</p>
                          </div>
                        ))}
                      </div>

                      {/* Total */}
                      <div className="border-t border-gray-100 pt-4 mb-5">
                        <div className="flex items-center justify-between">
                          <span className="text-base font-bold text-gray-900">Total</span>
                          <span className="text-2xl font-bold" style={{color: '#92400e'}}>${cartTotal.toFixed(2)}</span>
                        </div>
                      </div>

                      {/* Customer Info Form */}
                      {showCustomerForm && (
                        <div className="space-y-3 mb-5 border-t border-gray-100 pt-5">
                          <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                            Your Details
                          </h3>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <input
                                type="text" name="firstName" placeholder="First Name *"
                                value={customerInfo.firstName} onChange={handleCustomerChange}
                                className={`w-full px-3 py-2.5 text-sm rounded-lg border ${validationErrors.firstName ? 'border-red-300 bg-red-50/50' : 'border-gray-200'} focus:border-gray-400 focus:ring-0 outline-none transition bg-gray-50`}
                              />
                              {validationErrors.firstName && <p className="text-red-500 text-xs mt-1">{validationErrors.firstName}</p>}
                            </div>
                            <div>
                              <input
                                type="text" name="lastName" placeholder="Last Name *"
                                value={customerInfo.lastName} onChange={handleCustomerChange}
                                className={`w-full px-3 py-2.5 text-sm rounded-lg border ${validationErrors.lastName ? 'border-red-300 bg-red-50/50' : 'border-gray-200'} focus:border-gray-400 focus:ring-0 outline-none transition bg-gray-50`}
                              />
                              {validationErrors.lastName && <p className="text-red-500 text-xs mt-1">{validationErrors.lastName}</p>}
                            </div>
                          </div>
                          <div>
                            <input
                              type="email" name="email" placeholder="Email *"
                              value={customerInfo.email} onChange={handleCustomerChange}
                              className={`w-full px-3 py-2.5 text-sm rounded-lg border ${validationErrors.email ? 'border-red-300 bg-red-50/50' : 'border-gray-200'} focus:border-gray-400 focus:ring-0 outline-none transition bg-gray-50`}
                            />
                            {validationErrors.email && <p className="text-red-500 text-xs mt-1">{validationErrors.email}</p>}
                          </div>
                          <input
                            type="tel" name="phone" placeholder="Phone * (for WhatsApp)"
                            value={customerInfo.phone} onChange={handleCustomerChange}
                            className={`w-full px-3 py-2.5 text-sm rounded-lg border ${validationErrors.phone ? 'border-red-300 bg-red-50/50' : 'border-gray-200'} focus:border-gray-400 focus:ring-0 outline-none transition bg-gray-50`}
                          />
                          {validationErrors.phone && <p className="text-red-500 text-xs mt-1">{validationErrors.phone}</p>}
                          <textarea
                            name="notes" placeholder="Special instructions (optional)"
                            value={customerInfo.notes} onChange={handleCustomerChange}
                            rows={2}
                            className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-gray-400 focus:ring-0 outline-none transition resize-none bg-gray-50"
                          />
                        </div>
                      )}

                      {/* Error */}
                      {orderError && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                          <p className="text-red-700 text-sm">{orderError}</p>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="space-y-2.5">
                        <button
                          onClick={handleProceedToPayment}
                          disabled={orderLoading}
                          className="w-full font-bold py-3.5 px-4 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm tracking-wide hover:scale-[1.02] active:scale-[0.98]"
                          style={{background: 'linear-gradient(180deg, #fdf6ee 0%, #f5e6d0 100%)', color: '#6b4c0a', border: '1px solid rgba(200,170,110,0.35)', boxShadow: '0 8px 24px rgba(160, 130, 70, 0.12), 0 2px 4px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.7)'}}
                        >
                          {orderLoading ? (
                            <span className="flex items-center justify-center gap-2">
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-amber-400 border-t-white"></div>
                              Creating Order...
                            </span>
                          ) : showCustomerForm ? (
                            `Pay $${cartTotal.toFixed(2)} →`
                          ) : (
                            'Continue to Checkout →'
                          )}
                        </button>

                        <button
                          onClick={() => dispatch(clearCart())}
                          className="w-full text-gray-400 hover:text-red-500 font-medium py-2 text-xs transition tracking-wide uppercase"
                        >
                          Clear Cart
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TiffinMenu;
